import React from 'react'
import { useSelector } from 'react-redux'
import { CollectionItem, PlaylistItem } from 'mytours-read-common'

import AudioPlayerBarComponent from '../../audio/components/AudioPlayerBar'
import { tourOwnsAudio } from '../functions'

interface Props {
	collectionId: number
	// The tour's stops, or nothing while they are still downloading — the same list the stop rows are
	// drawn from, so what the bar recognises and what the pills recognise cannot disagree.
	collectionItems?: CollectionItem[]
}

/**
 * The single page layout's sticky bar, and the one state it is there for.
 *
 * This layout plays its own audio through the pill over each collapsed stop's thumbnail and the
 * transport inside the open row, so a bar for one of its own tracks would be a third control for a
 * sound already on screen twice. What those cannot reach is audio that belongs to no stop here — a
 * track started on another tab, another screen, another tour — which keeps playing when the reader
 * lands on this page and would otherwise have no control anywhere on it.
 *
 * Hence one condition, over current state and nothing else: the current track is not this tour's.
 * Playing a stop makes it false and the bar goes as that stop's pill takes over; the foreign track
 * having been stopped in the same breath is what makes those two the same event rather than a
 * handover anything here has to sequence.
 *
 * Presentational: nothing here touches playback, and the bar decides for itself whether to draw
 * anything — it shows only for a playing or paused track, and owns the body class that reserves room
 * for it at the bottom of the page. Standing down therefore takes the reserved space with it.
 */
const ForeignAudioPlayerBar: React.FC<Props> = ({ collectionId, collectionItems }) => {
	const current = useSelector(
		(state: { audioplayer: { playlist: Map<string, PlaylistItem>; currentPlaylistId: string } }) =>
			state.audioplayer.playlist.get(state.audioplayer.currentPlaylistId),
	)

	if (!current || tourOwnsAudio(current, collectionId, collectionItems)) {
		return null
	}

	return <AudioPlayerBarComponent />
}

export default ForeignAudioPlayerBar
